import { useEffect, useState, useRef } from "react";
import { Alert, Col } from "react-bootstrap";
import { Link } from 'react-router-dom';
import WarningMessagePopup from "../PopupComponent/WarningMessagePopup";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable, } from '@tanstack/react-table';
import { ReactComponent as CheckBoxUnselectedHover } from '../../Assets/img/CheckBoxUnselectedHover.svg';
import { ReactComponent as CheckBoxPartial } from '../../Assets/img/CheckBoxPartial.svg';
import { ReactComponent as CheckBoxPartialHover } from '../../Assets/img/CheckBoxPartialHover.svg';
import { ReactComponent as CheckBoxUnselected } from '../../Assets/img/CheckBoxUnselected.svg';
import { ReactComponent as CheckBoxSelected } from '../../Assets/img/CheckBoxSelected.svg';
import { ReactComponent as CheckBoxSelectedHover } from '../../Assets/img/CheckBoxSelectedHover.svg';
import { ReactComponent as DescendingIcon } from '../../Assets/img/DescendingIcon.svg';
import { ReactComponent as AscendingIcon } from '../../Assets/img/AscendingIcon.svg';
import { ReactComponent as DescendingHoverIcon } from '../../Assets/img/DescendingHoverIcon.svg';
import { ReactComponent as AscendingHoverIcon } from '../../Assets/img/AscendingHoverIcon.svg';
import { ReactComponent as Edit } from '../../Assets/img/EditPen.svg';
import { ReactComponent as EditPenHover } from '../../Assets/img/EditPenHover.svg';
import { ReactComponent as Filterclearbutton } from '../../Assets/img/Filter-input-clearbutton.svg';
import { ReactComponent as Filterclearbuttonhover } from '../../Assets/img/Filter-input-clearbutton-hover.svg';
import { ReactComponent as Show } from '../../Assets/img/Show.svg';
import { ReactComponent as Hide } from '../../Assets/img/Hide.svg';
import PageTop from "../PageComponent/PageTop";
import { CSVLink } from "react-csv";
import { ModalFooter } from 'react-bootstrap';

import ConfigsService from "../../Services/ConfigurationService";
import LoadingIndicator from "../CommonComponent/LoadingIndicator";
import NoMatch from "../CommonComponent/NoMatch";
import moment from 'moment';
import ReactPopup from 'reactjs-popup';
import DatePicker from "react-datepicker";
import Footer from "../FooterComponent/Footer";
import "bootstrap/dist/css/bootstrap.min.css";
import Popup from "../PopupComponent/Popup";
import MessagePopup from "../PopupComponent/MessagePopup";
import Navigation from "../NavigationComponent/Navigation";
import TopNavigation from "../NavigationComponent/TopNavigation";
import PageTable from "../PageComponent/PageTable";
import ReactCalendarPopup from "../PopupComponent/ReactCalendarPopup";
import EditConfig from "./EditConfiguration";
import AddConfig from "../ConfigurationComponent/AddConfiguration";
import TextFilterBox from "../PopupComponent/TextFilterBox";

const columnHelper = createColumnHelper();
const columns = [columnHelper.accessor('Config Key', {
    cell: info => <span style={{ width: info.column.getSize() }}>{info.getValue()}</span>,
    header: info => <span style={{ width: info.column.getSize() }}>Config Key</span>,
    id: "ConfigKey",
    meta: {
        sortFieldName: "ConfigKey",
        type: "string"
    },
    size: 250
}), columnHelper.accessor('Config Value', {
    cell: info => <span style={{ width: info.column.getSize() }}>{info.getValue()}</span>,
    header: info => <span style={{ width: info.column.getSize() }}>Config Value</span>,
    id: "ConfigValue",
    meta: {
        sortFieldName: "ConfigValue",
        type: "string"
    },
    size: 250
}), columnHelper.accessor('Created On', {
    cell: info => <span style={{ width: info.column.getSize() }}>{info.getValue() ? moment(info.getValue()).format('DD-MMM-YYYY') : null}</span>,
    header: info => <span style={{ width: info.column.getSize() }}>Created On</span>,
    id: "CreatedOn",
    meta: {
        sortFieldName: "CreatedDate",
        type: "date"
    },
    size: 225
}), columnHelper.accessor('Modified On', {
    cell: info => <span style={{ width: info.column.getSize() }}>{info.getValue() ? moment(info.getValue()).format('DD-MMM-YYYY') : null}</span>,
    header: info => <span style={{ width: info.column.getSize() }}>Modified On</span>,
    id: "ModifiedOn",
    meta: {
        sortFieldName: "ModifiedDate",
        type: "date"
    },
    size: 225
})];

/**
 * Renders configuration page with all the configs and config functions
 * @component
 */
function Configuration() {

    //#region States

    const [state, setState] = useState({
        status: 'LOADING',
        configList: null,
        configKey: null,
        popup: false,
        message: null,
        error: null,
        sortField: null,
        sortType: null,
        selectedItems: [],
        showFilter: false,
        allColumnFilter: null,
        searchConfigKey: "",
        configKeyFilter: null,
        searchConfigValue: "",
        configValueFilter: null,
        searchCreatedDate: "",
        createdDateFilter: null,
        searchModifiedDate: "",
        modifiedDateFilter: null,
        createdFromDate: 'DD/MM/YYYY',
        createdToDate: 'DD/MM/YYYY',
        modifiedFromDate: 'DD/MM/YYYY',
        modifiedToDate: 'DD/MM/YYYY',
        isTableLoading: false,
        model: null,
        perPage: 20,
        currentPage: 0,
        pageCount: 0,
        totalDataCount: 0,
        selectedItemsForPopup: [],
        hiddenColumns: [],
        allConfigs: [],
        triggerCSVDownload: false
    });

    //#endregion

    //#region Refs

    const searchValue = useRef();
    const createdDatePopupRef = useRef();
    const modifiedDatePopupRef = useRef();
    const csvRef = useRef();

    const [data, setData] = useState([]);

    const table = useReactTable({
        data,
        columns,
        columnResizeMode: "onChange",
        getCoreRowModel: getCoreRowModel(),
    });
    //#endregion

    //#region CSV headers

    const csvHeader = [
        { label: "Config Key", key: "ConfigKey" },
        { label: "Config Value", key: "ConfigValue" },
        { label: "Created On", key: "CreatedDate" },
        { label: "Modified On", key: "ModifiedDate" }
    ];

    //#endregion

    //#region useEffect hook

    /**
    * Calls loadConfigList() method to get config details
    * Runs on state change of state.currentPage, state.sortField, state.sortType, state.perPage, state.allColumnFilter, state.configKeyFilter,state.configValueFilter, state.createdDateFilter, state.modifiedDateFilter
    */
    useEffect(() => {
        async function fetchData() {
            await loadConfigList();
        }
        fetchData();
    }, [state.sortField, state.sortType, state.allColumnFilter, state.perPage, state.currentPage, state.configKeyFilter, state.configValueFilter, state.createdDateFilter,
    state.modifiedDateFilter]);

    /**
 * Calls loadConfigList() method to get config details on successfull execution of configuration service methods
 * Runs only on state change of state.success
 */
    useEffect(() => {
        async function fetchData() {
            await loadConfigList();
        }
        if (state.success == true) {
            fetchData();
        }
    }, [state.success]);

    useEffect(() => {
        if (state.triggerCSVDownload) {
            csvRef.current.link.click();
            setState((currentState) => ({
                ...currentState,
                triggerCSVDownload: false
            }));
        }
    }, [state.triggerCSVDownload])

    //#endregion

    //#region Methods

    /**
    * Calls config service to fetch configs according to config filters
    */
    const loadConfigList = async () => {
        setState((currentState) => ({
            ...currentState,
            isTableLoading: true,
        }));

        try {
            var request = {
                Filters: [state.allColumnFilter, state.configKeyFilter, state.configValueFilter, state.createdDateFilter,
                state.modifiedDateFilter].filter(f => f != null),
                Sort: (state.sortField == null || state.sortType == null) ? [] : [{
                    SortField: state.sortField,
                    sortType: state.sortType
                }],
                Pagination: {
                    PageStart: state.currentPage,
                    PageFetch: state.perPage
                }
            }

            const response = await ConfigsService.GetConfigsPerPage(request);

            var responseD = [];
            response.Data.forEach((config) => {
                responseD.push({
                    "Config Key": config.ConfigKey,
                    "Config Value": config.ConfigValue,
                    "Created On": config.CreatedDate,
                    "Modified On": config.ModifiedDate
                })
            })
            setData(responseD);

            console.log(response);

            setState((currentState) => ({
                ...currentState,
                isTableLoading: false,
                status: 'LOADED',
                configList: response.Data,
                pageCount: Math.ceil(
                    response.TotalRecord / state.perPage
                ),
                totalDataCount: response.TotalRecord
            }));
        } catch (error) {
            setState((currentState) => ({
                ...currentState,
                status: 'ERROR',
                error: error
            }));
        }
    }

    /**
    * Sort the given field  ("asc" to "desc", "desc" to "asc", null to "asc") 
    * @param   {String} field  Coloumn name that user wants to change 
    */
    const sort = (field) => {
        var type = state.sortType;
        var fieldValue = state.sortField;
        if (type == null) {
            type = "asc";
        }
        else if (type == "asc") {
            if (fieldValue == field) {
                type = "desc";
            }
            else {
                type = "asc";
            }
        }
        else {
            if (fieldValue == field) {
                type = null;
            }
            else {
                type = "asc";
            }
        }

        setState((currentState) => ({
            ...currentState,
            sortField: field,
            sortType: type,
            selectedItems: []
        }));
    }

    /**
    * Push all the configs into the list of selected configs 
    */
    const selectAllItems = () => {
        var selected = [];
        state.configList.forEach(config => {
            selected.push(config);
        });
        setState((currentState) => ({
            ...currentState,
            selectedItems: selected
        }));
    }

    /**
    * Remove all the configs from the list of selected configs 
    */
    const unSelectAllItems = () => {
        setState((currentState) => ({
            ...currentState,
            selectedItems: []
        }));
    }

    /**
   * Push the config into the list of selected configs 
   * @param   {Object} config  Object that contains ConfigKey,ConfigValue,CreatedDate,ModifiedDate
   */
    const selectItem = (config) => {
        var selected = state.selectedItems;
        selected.push(config);
        setState((currentState) => ({
            ...currentState,
            selectedItems: selected
        }));
    }

    /**
    * Remove the config from the list of selected configs 
    * @param   {Object} config  Object that contains ConfigKey,ConfigValue,CreatedDate,ModifiedDate
    */
    const unSelectItem = (config) => {
        var selected = state.selectedItems.filter(sel => sel != config);
        setState((currentState) => ({
            ...currentState,
            selectedItems: selected
        }));
    }

    /**
    * Search configuration
    */
    const searchAllColumn = () => {
        if (searchValue.current.value == "") {
            setState((currentState) => ({
                ...currentState,
                allColumnFilter: null
            }));
        }
        else {
            setState((currentState) => ({
                ...currentState,
                allColumnFilter: {
                    Type: "contains",
                    Value: searchValue.current.value,
                    ColumnNames: ["configkey", "configvalue", "createddate", "modifieddate"]
                }
            }));
        }
    }

    /**
    * Search config by config key
    * @param    {Event} event  User action (on changing value)
    */
    const searchByConfigKey = (event) => {
        setState((currentState) => ({
            ...currentState,
            searchConfigKey: event.target.value
        }));

        var filter;
        if (event.target.value == "") {
            filter = null;
        }
        else {
            filter = {
                ColumnNames: ["configkey"],
                Type: "contains",
                Value: event.target.value
            };
        }
        setState((currentState) => ({
            ...currentState,
            configKeyFilter: filter
        }));
    }

    /**
   * Search config by config value
   * @param    {Event} event  User action (on changing value)
   */
    const searchByConfigValue = (event) => {
        setState((currentState) => ({
            ...currentState,
            searchConfigValue: event.target.value
        }));

        var filter;
        if (event.target.value == "") {
            filter = null;
        }
        else {
            filter = {
                ColumnNames: ["configvalue"],
                Type: "contains",
                Value: event.target.value
            };
        }
        setState((currentState) => ({
            ...currentState,
            configValueFilter: filter
        }));
    }

    /**
  * Filter config by created date
  */
    const searchByCreatedDate = () => {
        createdDatePopupRef.current.close();
        setState((currentState) => ({
            ...currentState,
            createdDateFilter: {
                ColumnNames: ["createddate"],
                Type: "between",
                FromValue: moment(currentState.createdFromDate).format('DD/MM/YYYY'),
                ToValue: moment(currentState.createdToDate).format('DD/MM/YYYY')
            },
            searchCreatedDate: moment(currentState.createdFromDate).format('DD/MM/YY') + " To " + moment(currentState.createdToDate).format('DD/MM/YY')
        }));
    }

    /**
  * Filter config by modified date
  */
    const searchByModifiedDate = () => {
        modifiedDatePopupRef.current.close();
        setState((currentState) => ({
            ...currentState,
            modifiedDateFilter: {
                ColumnNames: ["modifieddate"],
                Type: "between",
                FromValue: moment(currentState.modifiedFromDate).format('DD/MM/YYYY'),
                ToValue: moment(currentState.modifiedToDate).format('DD/MM/YYYY')
            },
            searchModifiedDate: moment(currentState.modifiedFromDate).format('DD/MM/YY') + " To " + moment(currentState.modifiedToDate).format('DD/MM/YY')
        }));
    }

    /**
     * Move to next page
     */
    const nextPageHandler = () => {
        if (state.currentPage + 2 > state.pageCount) {
            return;
        }
        setState((currentState) => ({
            ...currentState,
            currentPage: currentState.currentPage + 1,
            selectedItems: []
        }));
    }

    /**
    * Move to previous page
    */
    const previousPageHandler = () => {
        if (state.currentPage - 1 < 0) {
            return;
        }
        setState((currentState) => ({
            ...currentState,
            currentPage: currentState.currentPage - 1,
            selectedItems: []
        }));
    }

    /**
    * Move to first page
    */
    const firstPageHandler = () => {
        if (state.currentPage == 0) {
            return;
        }
        setState((currentState) => ({
            ...currentState,
            currentPage: 0,
            selectedItems: []
        }));
    }

    /**
    * Move to last page
    */
    const lastPageHandler = () => {
        if (state.currentPage == state.pageCount - 1) {
            return;
        }
        setState((currentState) => ({
            ...currentState,
            currentPage: currentState.pageCount - 1,
            selectedItems: []
        }))
    }

    /**
    * Changes the number of configs to display per page according to user input
    * @param    {Event} event  User action (on changing value)
    */
    const changePerPage = (event) => {
        setState((currentState) => ({
            ...currentState,
            perPage: event.target.value,
            currentPage: 0,
            selectedItems: []
        }));
    }

    /**
    * Clear all the applied filters
    */
    const clearFilters = () => {
        setState((currentState) => ({
            ...currentState,
            configKeyFilter: null,
            configValueFilter: null,
            createdDateFilter: null,
            modifiedDateFilter: null,
            searchConfigKey: "",
            searchConfigValue: "",
            searchCreatedDate: "",
            searchModifiedDate: "",
            createdFromDate: 'DD/MM/YYYY',
            createdToDate: 'DD/MM/YYYY',
            modifiedFromDate: 'DD/MM/YYYY',
            modifiedToDate: 'DD/MM/YYYY'
        }))
    }

    /**
    * Show the selected column in configuration table
    * @param    {String} columnName  Column name that user selected to show
    */
    const showColumn = (columnName) => {
        if (columnName == "") {
            setState((currentState) => ({
                ...currentState,
                hiddenColumns: []
            }));
        }

        var columns = state.hiddenColumns.filter(c => c != columnName);

        setState((currentState) => ({
            ...currentState,
            hiddenColumns: columns
        }));
    }

    /** Hide the selected column from configuration table
    * @param    { String } columnName  Column name that user selected to hide
    */
    const hideColumn = (columnName) => {
        var columns = state.hiddenColumns;
        columns.push(columnName);

        setState((currentState) => ({
            ...currentState,
            hiddenColumns: columns
        }));
    }

    /**
    * Show all the columns in configuration table
    */
    const showAllColumns = () => {
        setState((currentState) => ({
            ...currentState,
            hiddenColumns: []
        }));
    }

    /**
    * Calls config service to fetch all configs
    */
    const getAllConfigs = async () => {
        ConfigsService.getConfigs().then((configs) => {
            setState((currentState) => ({
                ...currentState,
                allConfigs: configs,
                triggerCSVDownload: true
            }));
        }).catch((error) => {
            setState((currentState) => ({
                ...currentState,
                status: "ERROR",
                error: error,
            }));
        });
    }

    /**
   * Opens model
   * @param    {String} model  Name of model to open
   * @param    {String} param  Value of parameter corresponding to model
   */
    const modelOpenHandler = (model, param) => {
        if (model == "activate" || model == "deactivate") {
            setState((currentState) => ({
                ...currentState,
                configKey: param
            }));
        }
        else if (model == "edit") {
            setState((currentState) => ({
                ...currentState,
                configKey: param
            }));
        }

        setState((currentState) => ({
            ...currentState,
            model: model
        }));
    }

    /**
   * Display message
   * @param    {String} message  Message needs to be displayed
   */
    const messageDisplayHandler = async (message) => {
        setState((currentState) => ({
            ...currentState,
            selectedItems: [],
            selectedItemsForPopup: [],
            success: true,
            message: message
        }));
    }

    /**
    * Deletes all the selected configs
    */
    const DeleteConfigs = async () => {
        try {
            var Ids = [];
            state.selectedItems.forEach(d => Ids.push(d.ConfigId));
            var request = {
                Ids: Ids
            }
            await ConfigsService.BulkConfigDelete(request);

            setState((currentState) => ({
                ...currentState,
                selectedItems: [],
                model: null,
                success: true,
                message: "Configs deleted",
            }));
        } catch (error) {
            setState((currentState) => ({
                ...currentState,
                status: "ERROR",
                error: error,

            }));
        }
    };

    /**
   * Closes message popup
   */
    const messagePopupCloseHandler = () => {
        setState((currentState) => ({
            ...currentState,
            selectedItems: [],
            selectedItemsForPopup: [],
            success: null,
            message: null
        }));
    }

    /**
   * Closes opened model
   */
    const modelCloseHandler = async () => {
        setState((currentState) => ({
            ...currentState,
            model: null
        }));
    }

    /**
    * Reset the page to default
    */
    const resetPage = () => {
        setState((currentState) => ({
            ...currentState,
            status: "LOADED",
            error: null,
            perPage: 20,
            currentPage: 0,
            sortField: null,
            sortType: null,
            selectedItems: [],
            showFilter: false,
            allColumnFilter: null,
            configKeyFilter: null,
            configValueFilter: null,
            createdDateFilter: null,
            modifiedDateFilter: null,
            searchConfigKey: "",
            searchConfigValue: "",
            searchCreatedDate: "",
            searchModifiedDate: "",
            isTableLoading: false,
            createdFromDate: 'DD/MM/YYYY',
            createdToDate: 'DD/MM/YYYY',
            modifiedFromDate: 'DD/MM/YYYY',
            modifiedToDate: 'DD/MM/YYYY',
            model: null,
            success: null,
            message: null,
            hiddenColumns: [],
            allConfigs: [],
            selectedItemsForPopup: []
        }));
        searchValue.current.value = ""
        table.getHeaderGroups().map((headerGroup) => (
            headerGroup.headers.map((header) => (
                header.column.resetSize()
            ))
        ))
    }

    //#endregion

    return (
        <>
            {state.status == "LOADING" && <LoadingIndicator size="large" message="Fetching..." />}
            {state.status == "ERROR" && (state.error?.message?.indexOf("404") > -1 ? <NoMatch /> : <Alert variant="danger">{state.error?.message}</Alert>)}
            {state.status == "LOADED" &&
                <>
                    <Navigation />
                    <TopNavigation onPageReset={resetPage} />
                    <div className="page-container">
                        {state.model == "add" && <AddConfig onShowMessage={messageDisplayHandler} onClose={modelCloseHandler} action="Yes" />}
                        {state.model == "edit" && <EditConfig onShowMessage={messageDisplayHandler} onClose={modelCloseHandler} action="Yes" ConfigKey={state.configKey} />}
                        {state.success && <MessagePopup onClose={messagePopupCloseHandler} title="Success" body={<div className="message-popup-container-body"> {state.message}
                            {state.selectedItemsForPopup.length != 0 &&
                                <table className="popup-table">
                                    <tr>
                                        <th className="warning-message-popup-first">ConfigKey</th>
                                        <th className="warning-message-popup-last">ConfigValue</th>
                                    </tr>
                                    <tbody className="popup-tbody"> {state.selectedItemsForPopup?.slice(0, 3).map(configList => (
                                        <tr>
                                            <td className="warning-message-popup-first">
                                                {configList.ConfigKey}
                                            </td>
                                            <td className="warning-message-popup-last">
                                                {configList.ConfigValue}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                    <div className="bottom-title">
                                        {state.selectedItemsForPopup?.length > 3 && <p>... and {state.selectedItemsForPopup.length - 3} more {state.selectedItemsForPopup.length - 3 == 1 ? 'record' : 'records'}. </p>}
                                    </div>
                                </table>}
                        </div>} />}
                        {state.error != null && <MessagePopup title="Failure" body={state.error} />}
                        {state.model == "delete" && <WarningMessagePopup onClose={modelCloseHandler} title="Delete Configs" body={<div className="message-popup-container-body"> Do you want to Delete :
                            {state.selectedItems != null &&
                                <table className="popup-table">
                                    <tr>
                                        <th className="warning-message-popup-first">ConfigKey</th>
                                        <th className="warning-message-popup-last">ConfigValue</th>
                                    </tr>
                                    <tbody className="popup-tbody"> {state.selectedItems?.slice(0, 3).map(configList => (
                                        <tr>
                                            <td className="warning-message-popup-first">
                                                {configList.ConfigKey}
                                            </td>
                                            <td className="warning-message-popup-last">
                                                {configList.ConfigValue}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                    <div className="bottom-title">
                                        {state.selectedItems?.length > 3 && <p>... and {state.selectedItems.length - 3} more {state.selectedItems.length - 3 == 1 ? 'record' : 'records'} ? </p>}
                                    </div>
                                </table>}
                        </div>} action="Yes" onAction={DeleteConfigs} />}
                        <PageTop searchPlaceholder="Search Configurations"
                            disableUploadButton
                            disableSecondAddButton
                            addButtonText="Add Configuration"
                            searchRef={searchValue}
                            showFilter={state.showFilter}
                            modelOpenHandler={modelOpenHandler}
                            searchAllColumnHandler={searchAllColumn}
                            resetPageHandler={resetPage}
                            filterColumnHandler={() => {
                                setState((currentState) => ({ ...currentState, showFilter: !currentState.showFilter }));
                                if (state.showFilter) {
                                    clearFilters();
                                }
                            }}
                            menuData={<div className="menu-popup-container">
                                <span className="bold-underline-text" onClick={() => {
                                    showAllColumns();
                                }}>
                                    ShowAll
                                </span>
                                <div className="columns-container">
                                    {table.getHeaderGroups().map(headerGroup => (
                                        headerGroup.headers.map(header => (
                                            <div className="column-container">
                                                <span className="column-text">{header.column.columnDef.accessorKey}</span>
                                                <span className="column-icon">
                                                    {state.hiddenColumns?.includes(header.id) ?
                                                        <Hide onClick={() => {
                                                            showColumn(header.id);
                                                        }} />
                                                        : <Show style={{ opacity: 0.5 }} onClick={() => {
                                                            hideColumn(header.id);
                                                        }} />}
                                                </span>
                                            </div>
                                        ))
                                    ))}
                                </div>
                                <div style={{ margin: "0 8px 10px 8px", borderTop: "1px solid #ddd" }}></div>
                                <div className="columns-container">
                                    <div className="bottom-column-container">
                                        {state.configList.length === 0 ?
                                            (<span className="bottom-column-text" style={{ opacity: 0.5 }}>
                                                Export All...
                                            </span>) :
                                            (<>
                                                <span className="bottom-column-text" onClick={getAllConfigs} style={{ cursor: "pointer", maxWidth: "fit-content" }}>Export All...</span>
                                                <CSVLink
                                                    data={state.allConfigs}
                                                    headers={csvHeader}
                                                    separator={","}
                                                    filename={"Configs.csv"}
                                                    asyncOnClick={true}
                                                    ref={csvRef} target="_blank" ></CSVLink>
                                            </>)}
                                    </div>
                                    <div className="bottom-column-container">
                                        {state.selectedItems.length === 0 ?
                                            (<span className="bottom-column-text" style={{ opacity: 0.5 }}>
                                                Export Selected...
                                            </span>) :
                                            (<span>
                                                <CSVLink className="bottom-column-text"
                                                    data={state.selectedItems}
                                                    headers={csvHeader}
                                                    separator={","}
                                                    filename={"Configs.csv"}>
                                                    Export Selected...
                                                </CSVLink>
                                            </span>)}
                                    </div>
                                </div>
                            </div>} />
                        <PageTable isTableLoading={state.isTableLoading}
                            tableData={
                                <table>
                                    <thead className="table-header">
                                        <tr class="normal-header">
                                            <th scope="col" style={{ padding: "0 5px 0 5px" }}>
                                                {state.selectedItems.length == 0 ?
                                                    <Col className="menu-icon-container">
                                                        <CheckBoxUnselected onClick={selectAllItems} />
                                                        <CheckBoxUnselectedHover className="hover-icon" onClick={selectAllItems} />
                                                    </Col>
                                                    :
                                                    (state.selectedItems.length == state.configList.length ?
                                                        <Col className="menu-icon-container">
                                                            <CheckBoxSelected onClick={unSelectAllItems} />
                                                            <CheckBoxSelectedHover className="hover-icon" onClick={unSelectAllItems} />
                                                        </Col> :
                                                        <Col className="menu-icon-container">
                                                            <CheckBoxPartial onClick={selectAllItems} />
                                                            <CheckBoxPartialHover className="hover-icon" onClick={selectAllItems} />
                                                        </Col>)}
                                            </th>
                                            {table.getHeaderGroups().map(headerGroup => (
                                                headerGroup.headers.map(header => (
                                                    !state.hiddenColumns.includes(header.id) &&
                                                    <th key={header.id} scope="col"
                                                        style={{ width: header.getSize(), cursor: "pointer", textAlign: header.column.columnDef.meta.type == "date" ? "right" : (header.column.columnDef.meta.type == "bool" ? "center" : "left") }}>
                                                        <div className="header-text-wrapper" onClick={() => { sort(header.column.columnDef.meta.sortFieldName) }}>
                                                            {state.sortField == header.column.columnDef.meta.sortFieldName && state.sortType == "asc" ?
                                                                <div className={`${header.column.columnDef.meta.type == "date" ? 'sort-icon-left' : 'sort-icon-right'}`}>
                                                                    <DescendingIcon />
                                                                    <AscendingHoverIcon />
                                                                </div> : (state.sortField == header.column.columnDef.meta.sortFieldName && state.sortType == "desc" ?
                                                                    <div className={`${header.column.columnDef.meta.type == "date" ? 'sort-icon-left' : 'sort-icon-right'}`} >
                                                                        <DescendingHoverIcon />
                                                                        <AscendingIcon />
                                                                    </div> : <div className={`${header.column.columnDef.meta.type == "date" ? 'sort-icon-left' : 'sort-icon-right'}`} >
                                                                        <DescendingIcon />
                                                                        <AscendingIcon />
                                                                    </div>)}
                                                            {header.isPlaceholder
                                                                ? null
                                                                : flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                        </div>
                                                        <div className={`resize-handle ${header.column.getIsResizing() ? 'active' : null}`} onMouseDown={header.getResizeHandler()} onTouchStart={header.getResizeHandler()}></div>
                                                    </th>
                                                ))
                                            ))}
                                            <th scope="col"></th>
                                        </tr>
                                        {state.showFilter &&
                                            <tr class="filter-header">
                                                <th scope="col"></th>
                                                {!state.hiddenColumns.includes("ConfigKey") &&
                                                    <th scope="col" style={{ padding: "0 5px 0 5px" }} >
                                                        <TextFilterBox clearFilterHandler={() => {
                                                            setState((currentState) => ({
                                                                ...currentState,
                                                                searchConfigKey: "", configKeyFilter: null,
                                                            }));
                                                        }}
                                                            searchPlaceHolder={state.searchConfigKey}
                                                            searchHandler={searchByConfigKey} />
                                                    </th>}
                                                {!state.hiddenColumns.includes("ConfigValue") &&
                                                    <th scope="col" style={{ padding: "0 5px 0 5px" }} >
                                                        <TextFilterBox clearFilterHandler={() => {
                                                            setState((currentState) => ({
                                                                ...currentState,
                                                                searchConfigValue: "", configValueFilter: null,
                                                            }));
                                                        }}
                                                            searchPlaceHolder={state.searchConfigValue}
                                                            searchHandler={searchByConfigValue} />
                                                    </th>}
                                                {!state.hiddenColumns.includes("CreatedOn") &&
                                                    <th scope="col" style={{ padding: "0 5px 0 5px" }} >
                                                        <ReactCalendarPopup searchDatePlaceHolder={state.searchCreatedDate}
                                                            popupRef={createdDatePopupRef}
                                                            fromDateVar={state.createdFromDate}
                                                            toDateVar={state.createdToDate}
                                                            searchHandler={searchByCreatedDate}
                                                            setFromDateHandler={(date) => setState((currentState) => ({ ...currentState, createdFromDate: date }))}
                                                            setToDateHandler={(date) => setState((currentState) => ({ ...currentState, createdToDate: date }))}
                                                            resetHandler={() => { setState((currentState) => ({ ...currentState, createdFromDate: 'DD/MM/YYYY', createdToDate: 'DD/MM/YYYY' })) }}
                                                            filterClearHandler={() => {
                                                                setState((currentState) => ({
                                                                    ...currentState,
                                                                    searchCreatedDate: "",
                                                                    createdDateFilter: null,
                                                                    createdFromDate: 'DD/MM/YYYY',
                                                                    createdToDate: 'DD/MM/YYYY',
                                                                }))
                                                            }} />
                                                    </th>}
                                                {!state.hiddenColumns.includes("ModifiedOn") &&
                                                    <th scope="col" style={{ padding: "0 5px 0 5px" }}>
                                                        <ReactCalendarPopup searchDatePlaceHolder={state.searchModifiedDate}
                                                            popupRef={modifiedDatePopupRef}
                                                            fromDateVar={state.modifiedFromDate}
                                                            toDateVar={state.modifiedToDate}
                                                            searchHandler={searchByModifiedDate}
                                                            setFromDateHandler={(date) => setState((currentState) => ({ ...currentState, modifiedFromDate: date }))}
                                                            setToDateHandler={(date) => setState((currentState) => ({ ...currentState, modifiedToDate: date }))}
                                                            resetHandler={() => { setState((currentState) => ({ ...currentState, modifiedFromDate: 'DD/MM/YYYY', modifiedToDate: 'DD/MM/YYYY' })) }}
                                                            filterClearHandler={() => {
                                                                setState((currentState) => ({
                                                                    ...currentState,
                                                                    searchModifiedDate: "",
                                                                    modifiedDateFilter: null,
                                                                    modifiedFromDate: 'DD/MM/YYYY',
                                                                    modifiedToDate: 'DD/MM/YYYY',
                                                                }))
                                                            }} />
                                                    </th>}
                                                <th scope="col"></th>
                                            </tr >
                                        }
                                    </thead>
                                    {!state.isTableLoading &&
                                        <tbody >
                                            {table.getRowModel().rows.map(row => (
                                                <tr key={row.id} className={state.selectedItems.find(sel => sel.ConfigKey == state.configList[row.id].ConfigKey && sel.ConfigValue == state.configList[row.id].ConfigValue) == null ? '' : 'selected'}>
                                                    <td scope="col">
                                                        {state.selectedItems.find(sel => sel.ConfigKey == state.configList[row.id].ConfigKey && sel.ConfigValue == state.configList[row.id].ConfigValue) == null ?
                                                            <Col className="menu-icon-container">
                                                                <CheckBoxUnselected onClick={() => { selectItem(state.configList[row.id]) }} />
                                                                <CheckBoxUnselectedHover className="hover-icon" onClick={() => { selectItem(state.configList[row.id]) }} />
                                                            </Col>
                                                            :
                                                            <Col className="menu-icon-container">
                                                                <CheckBoxSelected onClick={() => { unSelectItem(state.configList[row.id]) }} />
                                                                <CheckBoxSelectedHover className="hover-icon" onClick={() => { unSelectItem(state.configList[row.id]) }} />
                                                            </Col>
                                                        }
                                                    </td>
                                                    {row.getVisibleCells().map(cell => (
                                                        !state.hiddenColumns.includes(cell.column.id) &&
                                                        (cell.column.columnDef.meta.type == "date" ?
                                                            <td scope="col" key={cell.id} style={{ textAlign: "right" }}>
                                                                <div className="body-text-wrapper">
                                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                </div>
                                                            </td> :
                                                            <td scope="col" key={cell.id}>
                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                            </td>)
                                                    )
                                                    )}
                                                    <td scope="col">
                                                        <span className="menu-icon-container">
                                                            <Edit className="menu-icon" onClick={() => modelOpenHandler("edit", state.configList[row.id].ConfigKey)} />
                                                            <EditPenHover className="menu-icon hover-icon" onClick={() => modelOpenHandler("edit", state.configList[row.id].ConfigKey)} />
                                                        </span>
                                                    </td>
                                                </tr>

                                            ))}
                                        </tbody>}
                                </table>
                            } />
                        <Footer onFirstClick={firstPageHandler}
                            onLastClick={lastPageHandler}
                            onPreviousClick={previousPageHandler}
                            onNextClick={nextPageHandler}
                            perPage={state.perPage}
                            totalData={state.totalDataCount}
                            showingItemsCount={state.configList.length}
                            currentPage={state.currentPage + 1}
                            totalPage={state.pageCount}
                            selectedItems={state.selectedItems.length}
                            changePerPage={changePerPage}
                            modelOpenHandler={modelOpenHandler}
                            disableEditButton
                            disableLine
                        />
                    </div>
                </>
            }
        </>
    )
}

export default Configuration;